/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { getBackendUrl } from "@Utils";
import { useEffect, useState, useRef, forwardRef } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import { CONVERSION_STATUS_MAPPER } from "@Services/constants";
import { BiSolidPlusCircle } from "react-icons/bi";
import { PiDotsThreeCircleFill } from "react-icons/pi";
import { IoIosCheckmarkCircle } from "react-icons/io";
import axiosWrapper from "@Services/axiosWrapper";
import type { GetFileVersionDataResponse } from "@Types/fileOperations";
import { untranslated } from "@i18n";

interface FileConversionStatusProps {
  /** A key of `CONVERSION_STATUS_MAPPER`. Optional because `selectedFileItemSchema` declares it so. */
  conversionStatus?: string;
  /** `files.versions._id` of the version being polled. */
  fileVersion?: string;
}

/** What the forwarded ref holds: the id of the in-flight poll timeout, or `null` between polls. */
type PollingTimeout = ReturnType<typeof setTimeout> | null;

const FileConversionStatus = forwardRef<PollingTimeout, FileConversionStatusProps>((props, ref) => {
  const [conversionStatus, setConversionStatus] = useState(props.conversionStatus);
  const [tooltipContent, setTooltipContent] = useState(untranslated(""));
  const localRef = useRef<PollingTimeout>(null);
  // `forwardRef` also permits a callback ref, which has no `.current`. The only caller passing a ref
  // (`useFileManager`'s `pollingRef`) passes an object ref, which is what the writes below assume.
  const pollingRef = (ref || localRef) as React.MutableRefObject<PollingTimeout>; // Ref to store the timeout ID
  const isMounted = useRef(true); // Track if the component is mounted

  useEffect(() => {
    isMounted.current = true;
    const status = conversionStatus as keyof typeof CONVERSION_STATUS_MAPPER;
    setTooltipContent(untranslated(CONVERSION_STATUS_MAPPER[status]));
    startPolling(conversionStatus);

    return () => {
      isMounted.current = false;
      if (pollingRef.current) {
        clearTimeout(pollingRef.current); // Clear any pending timeout
      }
    };
  }, [conversionStatus]);

  const startPolling = (currentStatus: string | undefined) => {
    // The array is widened rather than asserting `currentStatus` non-null: `includes(undefined)`
    // returning `false` is exactly the intent here.
    if (!(["IN_PROGRESS", "NOT_STARTED"] as (string | undefined)[]).includes(currentStatus)) return;

    pollingRef.current = setTimeout(async () => {
      if (!isMounted.current) return;

      try {
        const response = await axiosWrapper.post<GetFileVersionDataResponse>(
          `${getBackendUrl()}/fileOperations/getFileVersionData`,
          {
            fileVersionId: props.fileVersion,
          },
        );
        const latestData = response.data.data;

        if (isMounted.current && latestData?.conversionStatus) {
          setConversionStatus(latestData.conversionStatus);
          startPolling(latestData.conversionStatus);
        }
      } catch (error) {
        console.error("Error fetching file version data:", error);
        Promise.reject(error);
      }
    }, 10000);
  };

  return (
    <ToolTipComponent showTipPointer position="bottom" content={tooltipContent} style={{ display: "inline-flex" }}>
      {(["RETRY", "IN_PROGRESS"] as (string | undefined)[]).includes(conversionStatus) && (
        <CircularProgress disableShrink size={16} />
      )}
      {conversionStatus === "ERRORED" && (
        <BiSolidPlusCircle style={{ transform: "rotate(45deg)" }} color="var(--negative-communication)" size={16} />
      )}
      {conversionStatus === "COMPLETED" && <IoIosCheckmarkCircle color="var(--positive-communication)" size={16} />}
      {conversionStatus === "NOT_STARTED" && <PiDotsThreeCircleFill color="var(--primary-gradient-color2)" size={16} />}
    </ToolTipComponent>
  );
});
FileConversionStatus.displayName = "FileConversionStatus";
export default FileConversionStatus;
